import React, { useState, useEffect } from 'react';
import { Link } from 'gatsby';
import { PAINT_PLAN } from '../utils/routeConstants';

function clamp(val, min, max) {
  return Math.max(min, Math.min(max, val));
}

function normalizePercent(percent) {
  if (typeof percent !== 'number' || Number.isNaN(percent)) return 0;
  return percent <= 1 ? percent * 100 : percent;
}

const LOCALSTORAGE_KEY = 'paintGoalWidgetOpen';

const PaintGoalWidget = ({ year, title }) => {
  async function getPaintGoals(targetYear) {
    try {
      const response = await fetch(`/api/paint-goals?year=${targetYear}`);
      if (!response.ok) {
        throw new Error(`Request failed with status ${response.status}`);
      }

      const data = await response.json();
      return data;
    } catch (error) {
      console.error('Error fetching paint goals:', error);
      return null;
    }
  }

  const [open, setOpen] = useState(true);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const currentYear = Number.isFinite(Number(year))
    ? Number(year)
    : new Date().getFullYear();

  // Load state from localStorage on mount
  useEffect(() => {
    (async () => {
      setLoading(true);
      const responseData = await getPaintGoals(currentYear);
      setData(responseData);
      setLoading(false);
    })();

    if (typeof window !== 'undefined') {
      const stored = window.localStorage.getItem(LOCALSTORAGE_KEY);
      if (stored === 'false') setOpen(false);
    }
  }, [currentYear]);

  // Store state in localStorage when changed
  useEffect(() => {
    if (typeof window !== 'undefined') {
      window.localStorage.setItem(LOCALSTORAGE_KEY, open ? 'true' : 'false');
    }
  }, [open]);

  // Minimized icon box
  if (!open) {
    return (
      <div
        style={{
          position: 'fixed',
          bottom: 24,
          right: 24,
          zIndex: 9999,
          background: 'white',
          border: '1px solid #ccc',
          borderRadius: 8,
          boxShadow: '0 2px 8px #0002',
          padding: '0.5em 0.7em',
          fontFamily: 'monospace',
          minWidth: 0,
          minHeight: 0,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          cursor: 'pointer',
        }}
        onClick={() => setOpen(true)}
        title="Show paint goal widget"
        aria-label="Show paint goal widget"
      >
        <span
          style={{ fontSize: 22, lineHeight: 1 }}
          role="img"
          aria-label="Paint Brush"
        >
          🖌️
        </span>
      </div>
    );
  }

  if (!data) {
    return (
      <div
        style={{
          position: 'fixed',
          bottom: 24,
          right: 24,
          zIndex: 9999,
          background: 'white',
          border: '1px solid #ccc',
          borderRadius: 8,
          boxShadow: '0 2px 8px #0002',
          padding: '1em 1.5em',
          fontFamily: 'monospace',
          minWidth: 220,
        }}
      >
        <button
          onClick={() => setOpen(false)}
          aria-label="Close"
          style={{
            position: 'absolute',
            top: 6,
            right: 8,
            background: 'none',
            border: 'none',
            fontSize: 18,
            cursor: 'pointer',
            color: '#888',
          }}
        >
          ×
        </button>
        <div style={{ fontWeight: 'bold' }}>
          {title || `Paint Goal ${currentYear}`}
        </div>
        {loading ? (
          <div style={{ color: '#888' }}>Loading paint goals...</div>
        ) : (
          <div style={{ color: '#b87333' }}>No data for {currentYear}.</div>
        )}
      </div>
    );
  }

  const { name, painted, goal, percent } = data;
  // percent comes in as a float like 0.75 so we convert to 75 and round for display
  const percentInt = Math.round(clamp(normalizePercent(percent), 0, 100));
  const barLength = 20;
  const filled = Math.round((percentInt / 100) * barLength);
  const empty = barLength - filled;
  console.log({
    percentInt,
    filled,
    empty,
  });

  const bar = '█'.repeat(filled) + '░'.repeat(empty < 0 ? 0 : empty);

  return (
    <div
      style={{
        position: 'fixed',
        bottom: 24,
        right: 24,
        zIndex: 9999,
        background: 'white',
        border: '1px solid #ccc',
        borderRadius: 8,
        boxShadow: '0 2px 8px #0002',
        padding: '1em 1.5em',
        fontFamily: 'monospace',
        minWidth: 220,
        lineHeight: 1.6,
      }}
    >
      <button
        onClick={() => setOpen(false)}
        aria-label="Close"
        style={{
          position: 'absolute',
          top: 6,
          right: 8,
          background: 'none',
          border: 'none',
          fontSize: 18,
          cursor: 'pointer',
          color: '#888',
        }}
      >
        ×
      </button>
      <div style={{ fontWeight: 'bold' }}>
        {title || name || `Paint Goal ${currentYear}`}
      </div>
      <div>
        {painted} / {goal} painted
      </div>
      <div style={{ fontSize: '1.2em', letterSpacing: '1px' }}>
        {bar} {percentInt}%
      </div>
      <div style={{ marginTop: 8 }}>
        <Link
          to={`${PAINT_PLAN}?year=${currentYear}`}
          style={{ color: '#7a4e18' }}
        >
          View full plan
        </Link>
      </div>
    </div>
  );
};

export default PaintGoalWidget;
